/**
 * One-way device-calendar sync orchestration (#1715, epic #2344).
 *
 * The care schedule always comes from the API (appointments + medications);
 * this hook only mirrors the upcoming window into the patient's chosen
 * writable device calendar and reconciles Louise-created events on re-sync.
 * Louise's own in-app/push reminders are untouched — calendar alerts are
 * additive.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import {
  fetchPatientMedications,
  patientMedicationsQueryKey,
} from "@src/packages/care/hooks/usePatientMedications";
import { useTimezone } from "@src/packages/preferences";
import { useNotificationPreferences } from "@src/packages/notifications/hooks/useNotificationPreferences";
import { timingToMinutesBefore } from "@src/packages/utils/reminder-timing";
import {
  buildAppointmentEventPlans,
  buildMedicationEventPlans,
  computeCalendarExportWindow,
  planCalendarSync,
} from "./calendar-export";
import {
  clearCalendarSyncState,
  readCalendarSyncState,
  useCalendarSyncState,
  writeCalendarSyncState,
} from "./calendar-sync-storage";
import {
  applyCalendarSyncPlan,
  getCalendarPermissionState,
  isCalendarSyncSupported,
  listWritableCalendars,
  removeAllSyncedEvents,
  requestCalendarPermission,
  type CalendarPermissionState,
  type WritableDeviceCalendar,
} from "./device-calendar";

export type CalendarSyncCopy = {
  /** Title for an appointment the patient saved without one. */
  appointmentFallbackTitle: string;
  /** Title for a medication record with no resolvable name. */
  medicationFallbackTitle: string;
  /** Location label for online appointments. */
  onlineLocationLabel: string;
};

export type CalendarSyncResult = {
  created: number;
  updated: number;
  deleted: number;
  failedCount: number;
  totalSynced: number;
};

function toAlarmMinutes(timings: unknown, fallback: string[]): number[] {
  const list =
    Array.isArray(timings) && timings.length > 0
      ? timings.filter((value): value is string => typeof value === "string")
      : fallback;
  const minutes = list.map((timing) => timingToMinutesBefore(timing));
  return [...new Set(minutes)].sort((a, b) => a - b);
}

export function useCalendarSync(copy: CalendarSyncCopy) {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const supported = isCalendarSyncSupported();
  const { state, isLoading: isStateLoading } = useCalendarSyncState(
    supported ? patientId : undefined,
  );
  const [permissionState, setPermissionState] =
    useState<CalendarPermissionState>(
      supported ? "not_requested" : "unsupported",
    );

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void getCalendarPermissionState().then((value) => {
      if (!cancelled) setPermissionState(value);
    });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const timezone = useTimezone();
  const { data: notificationPreferences } = useNotificationPreferences();
  const isConnected = Boolean(state);

  // The query range is the bounded export window. Recomputed per render is
  // fine (it only changes at patient-local midnight); the sync itself always
  // recomputes a fresh window.
  const window = useMemo(
    () => computeCalendarExportWindow(timezone),
    [timezone],
  );

  const appointmentsQuery = useAppointments({
    startDate: window.startDate,
    endDate: window.endDate,
    enabled: supported && isConnected,
  });

  const refreshPermission = useCallback(async () => {
    const value = await getCalendarPermissionState();
    setPermissionState(value);
    return value;
  }, []);

  const requestPermission = useCallback(async () => {
    const value = await requestCalendarPermission();
    setPermissionState(value);
    return value;
  }, []);

  const syncMutation = useMutation({
    mutationFn: async (): Promise<CalendarSyncResult> => {
      if (!tenantId || !patientId) {
        throw new Error("calendar-sync: not signed in");
      }
      const currentState = await readCalendarSyncState(patientId);
      if (!currentState) throw new Error("calendar-sync: not configured");
      const permission = await getCalendarPermissionState();
      setPermissionState(permission);
      if (permission !== "granted") {
        throw new Error("calendar-sync: permission unavailable");
      }

      // Always sync from fresh server data, not a possibly stale cache. The
      // server selects the medications active in the window this sync
      // computes, so a screen left open past midnight still exports the
      // window's newest day.
      const freshWindow = computeCalendarExportWindow(timezone);
      const medicationWindow = {
        from: freshWindow.startDate,
        to: freshWindow.endDate,
      };
      const [appointmentsResult, medications] = await Promise.all([
        appointmentsQuery.refetch(),
        queryClient.fetchQuery({
          queryKey: patientMedicationsQueryKey(
            tenantId,
            patientId,
            medicationWindow,
          ),
          queryFn: () =>
            fetchPatientMedications(
              client,
              tenantId,
              patientId,
              medicationWindow,
            ),
          staleTime: 0,
        }),
      ]);
      if (appointmentsResult.error) throw appointmentsResult.error;
      const appointmentAlarms = toAlarmMinutes(
        notificationPreferences?.appointmentTimings,
        ["1h_before"],
      );
      const medicationAlarms = toAlarmMinutes(
        notificationPreferences?.medicationTimings,
        ["exact"],
      );

      const desired = [
        ...buildAppointmentEventPlans(appointmentsResult.data ?? [], {
          window: freshWindow,
          patientTimeZone: timezone,
          alarmsMinutesBefore: appointmentAlarms,
          fallbackTitle: copy.appointmentFallbackTitle,
          onlineLocationLabel: copy.onlineLocationLabel,
        }),
        ...buildMedicationEventPlans(medications, {
          window: freshWindow,
          patientTimeZone: timezone,
          alarmsMinutesBefore: medicationAlarms,
          fallbackTitle: copy.medicationFallbackTitle,
        }),
      ];

      const plan = planCalendarSync(desired, currentState.events);
      const applied = await applyCalendarSyncPlan(
        currentState.calendarId,
        plan,
      );

      await writeCalendarSyncState(patientId, {
        calendarId: currentState.calendarId,
        calendarTitle: currentState.calendarTitle,
        events: applied.events,
        lastSyncedAt: new Date().toISOString(),
        lastSyncFailedCount: applied.failedCount,
      });

      return {
        created: applied.created,
        updated: applied.updated,
        deleted: applied.deleted,
        failedCount: applied.failedCount,
        totalSynced: Object.keys(applied.events).length,
      };
    },
  });

  const { mutateAsync: syncMutateAsync } = syncMutation;

  const connect = useCallback(
    async (calendar: WritableDeviceCalendar): Promise<CalendarSyncResult> => {
      if (!patientId) throw new Error("calendar-sync: not signed in");
      await writeCalendarSyncState(patientId, {
        calendarId: calendar.id,
        calendarTitle: calendar.title,
        events: {},
        lastSyncedAt: null,
        lastSyncFailedCount: 0,
      });
      return syncMutateAsync();
    },
    [patientId, syncMutateAsync],
  );

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!patientId) return;
      const currentState = await readCalendarSyncState(patientId);
      if (currentState) {
        await removeAllSyncedEvents(currentState.events);
      }
      await clearCalendarSyncState(patientId);
    },
  });

  return {
    isSupported: supported,
    isStateLoading: supported && isStateLoading,
    isConnected,
    state,
    permissionState,
    refreshPermission,
    requestPermission,
    listWritableCalendars,
    connect,
    syncNow: syncMutateAsync,
    isSyncing: syncMutation.isPending,
    syncError: syncMutation.error,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
  };
}
